import type {
  Centavos,
  FormaPagamento,
  Origem,
  Papel,
  StatusConexao,
  StatusTransacao,
  TipoTransacao,
} from '@truefinance/shared';

/**
 * Cliente da API.
 *
 * A sessão vive em cookie httpOnly, então não há token para o JavaScript
 * guardar nem anexar — basta `credentials: 'include'`. É o que torna a sessão
 * inalcançável por XSS.
 *
 * Em 401, tentamos o refresh **uma vez** e repetimos a requisição. Uma única
 * tentativa é proposital: se o refresh também falhar, insistir só produz laço.
 */

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    mensagem: string,
    /** Código mostrado ao usuário para citar no suporte. */
    readonly codigoSuporte?: string,
  ) {
    super(mensagem);
    this.name = 'ApiError';
  }

  get expirou(): boolean {
    return this.status === 401;
  }

  get semPermissao(): boolean {
    return this.status === 403;
  }
}

let refreshEmAndamento: Promise<boolean> | null = null;

async function tentarRefresh(): Promise<boolean> {
  // Várias requisições podem receber 401 ao mesmo tempo; um refresh só atende
  // todas. Sem isso, N requisições simultâneas disparariam N rotações de token,
  // e a detecção de reúso derrubaria a sessão inteira.
  refreshEmAndamento ??= fetch(`${BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      setTimeout(() => {
        refreshEmAndamento = null;
      }, 0);
    });

  return refreshEmAndamento;
}

async function requisitar<T>(
  caminho: string,
  opcoes: RequestInit = {},
  jaTentouRefresh = false,
): Promise<T> {
  const resposta = await fetch(`${BASE}${caminho}`, {
    ...opcoes,
    credentials: 'include',
    headers: {
      ...(opcoes.body ? { 'content-type': 'application/json' } : {}),
      ...opcoes.headers,
    },
  });

  if (resposta.status === 401 && !jaTentouRefresh && !caminho.includes('/auth/')) {
    if (await tentarRefresh()) return requisitar<T>(caminho, opcoes, true);
  }

  if (!resposta.ok) {
    const corpo = (await resposta.json().catch(() => ({}))) as {
      erro?: string;
      codigo_suporte?: string;
    };
    throw new ApiError(
      resposta.status,
      corpo.erro ?? 'Não conseguimos concluir sua solicitação.',
      corpo.codigo_suporte,
    );
  }

  if (resposta.status === 204) return undefined as T;
  return (await resposta.json()) as T;
}

const get = <T>(caminho: string) => requisitar<T>(caminho);
const post = <T>(caminho: string, corpo?: unknown) =>
  requisitar<T>(caminho, { method: 'POST', ...(corpo ? { body: JSON.stringify(corpo) } : {}) });
const patch = <T>(caminho: string, corpo: unknown) =>
  requisitar<T>(caminho, { method: 'PATCH', body: JSON.stringify(corpo) });
const requisitarPut = <T>(caminho: string, corpo: unknown) =>
  requisitar<T>(caminho, { method: 'PUT', body: JSON.stringify(corpo) });
const remover = <T>(caminho: string, corpo: unknown) =>
  requisitar<T>(caminho, { method: 'DELETE', body: JSON.stringify(corpo) });

// ---------------------------------------------------------------------------
// Tipos das respostas
// ---------------------------------------------------------------------------

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  orgId?: string;
}

export interface Transacao {
  id: string;
  tipo: TipoTransacao;
  valorCentavos: Centavos;
  descricao: string;
  categoriaId: string | null;
  contraparte: string | null;
  formaPagamento: FormaPagamento | null;
  dataOcorrencia: string;
  status: StatusTransacao;
  confianca: number | null;
  origem: Origem;
  criadaEm: string;
  deletedAt: string | null;
}

export interface Categoria {
  id: string;
  nome: string;
  tipo: TipoTransacao;
  cor: string;
  icone: string | null;
}

export interface Dashboard {
  mes: {
    entradas: Centavos;
    saidas: Centavos;
    saldo: Centavos;
    rotulo: { de: string; ate: string };
  };
  saldoTotal: Centavos;
  pendentesRevisao: number;
  lancamentosHoje: number;
  ultimasTransacoes: Transacao[];
  saidasPorCategoria: Array<{
    categoriaId: string | null;
    total: Centavos;
    quantidade: number;
    nome: string;
    cor: string;
  }>;
}

export interface ItemRevisao {
  transacao: Transacao;
  categoria: string | null;
  mensagemTexto: string | null;
  mensagemTranscricao: string | null;
  mensagemTipo: string | null;
}

export interface Conexao {
  id: string;
  status: StatusConexao;
  telefone: string | null;
  conectadaEm: string | null;
  ultimaMensagemEm: string | null;
  ultimoErro: string | null;
}

export type ModoAutomacao = 'financeiro' | 'chamados';

export interface ModeloChamado {
  id: string;
  nome: string;
  descricaoModelo: string | null;
  gatilhos: string[];
  navegacao: string[];
  camposFixos: Record<string, string>;
  instrucaoDescricao: string | null;
  ativo: boolean;
  usos: number;
}

export interface FiltroTransacoes {
  tipo?: TipoTransacao;
  status?: StatusTransacao;
  categoriaId?: string;
  de?: string;
  ate?: string;
  busca?: string;
  pagina?: number;
  porPagina?: number;
}

// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------

export const api = {
  login: (email: string, senha: string) =>
    post<{ usuario: Usuario }>('/api/v1/auth/login', { email, senha }),
  registrar: (dados: { organizacao: string; nome: string; email: string; senha: string }) =>
    post<{ usuario: Usuario }>('/api/v1/auth/registrar', dados),
  registroAberto: () => get<{ aberto: boolean }>('/api/v1/auth/registro-aberto'),
  logout: () => post<{ ok: true }>('/api/v1/auth/logout'),
  eu: () => get<{ usuario: Usuario }>('/api/v1/auth/me'),

  dashboard: () => get<Dashboard>('/api/v1/dashboard'),

  transacoes: (filtro: FiltroTransacoes = {}) => {
    const query = new URLSearchParams();
    for (const [chave, valor] of Object.entries(filtro)) {
      if (valor !== undefined && valor !== '') query.set(chave, String(valor));
    }
    const sufixo = query.toString();
    return get<{
      transacoes: Transacao[];
      paginacao: { pagina: number; porPagina: number; total: number; paginas: number };
    }>(`/api/v1/transacoes${sufixo ? `?${sufixo}` : ''}`);
  },

  editarTransacao: (id: string, dados: Record<string, unknown> & { motivo: string }) =>
    patch<{ transacao: Transacao }>(`/api/v1/transacoes/${id}`, dados),
  excluirTransacao: (id: string, motivo: string) =>
    remover<{ ok: true }>(`/api/v1/transacoes/${id}`, { motivo }),

  revisao: () => get<{ pendentes: ItemRevisao[] }>('/api/v1/revisao'),
  confirmarRevisao: (id: string) =>
    post<{ transacao: Transacao }>(`/api/v1/revisao/${id}/confirmar`),
  descartarRevisao: (id: string, motivo: string) =>
    post<{ ok: true }>(`/api/v1/revisao/${id}/descartar`, { motivo }),

  categorias: () => get<{ categorias: Categoria[] }>('/api/v1/categorias'),

  conexao: () => get<{ instancia: Conexao | null }>('/api/v1/conexao'),
  statusConexao: () =>
    get<{
      status: StatusConexao;
      telefone: string | null;
      ultimaMensagemEm: string | null;
      /** Sessão caída de um jeito que só refazer resolve — pedir QR não adianta. */
      precisaRecriar?: boolean;
      erro?: string;
    }>('/api/v1/conexao/status'),
  criarConexao: (numero?: string) =>
    post<{ instancia: Conexao; qrcode: string | null; codigoPareamento: string | null }>(
      '/api/v1/conexao',
      numero ? { numero } : undefined,
    ),
  qrcode: () =>
    get<{ qrcode: string | null; codigoPareamento: string | null; status: StatusConexao }>(
      '/api/v1/conexao/qrcode',
    ),
  refazerConexao: () => post<{ ok: true }>('/api/v1/conexao/refazer'),

  automacao: () =>
    get<{
      modo: ModoAutomacao;
      // A senha do portal nunca vem — nem cifrada, nem mascarada.
      credencial: {
        usuario: string;
        urlPortal: string;
        atualizadaEm: string;
        ultimoUsoEm: string | null;
        ultimoErro: string | null;
      } | null;
      modelos: ModeloChamado[];
    }>('/api/v1/automacao'),

  definirModoAutomacao: (modo: ModoAutomacao) =>
    requisitarPut<{ modo: ModoAutomacao }>('/api/v1/automacao/modo', { modo }),

  salvarCredencialPortal: (dados: { usuario: string; senha: string; urlPortal: string }) =>
    requisitarPut<{ ok: true; usuario: string }>('/api/v1/automacao/credencial', dados),

  criarModeloChamado: (dados: Record<string, unknown>) =>
    post<{ modelo: ModeloChamado }>('/api/v1/automacao/modelos', dados),

  excluirModeloChamado: (id: string) =>
    remover<{ ok: true }>(`/api/v1/automacao/modelos/${id}`, {}),

  chamados: () =>
    get<{
      chamados: Array<{
        id: string;
        numero: string | null;
        status: string;
        relatoOriginal: string | null;
        descricaoEnviada: string | null;
        erro: string | null;
        criadoEm: string;
        abertoEm: string | null;
        modelo: string | null;
      }>;
    }>('/api/v1/chamados'),
  desconectar: (motivo: string) => post<{ ok: true }>('/api/v1/conexao/desconectar', { motivo }),

  auditoria: (limite = 50) =>
    get<{
      registros: Array<{
        id: string;
        acao: string;
        entidade: string;
        entidadeId: string | null;
        atorNome: string | null;
        motivo: string | null;
        criadoEm: string;
      }>;
    }>(`/api/v1/auditoria?limite=${limite}`),
};
